/**
 * CardFieldsEditorBody — the field list + live card preview.
 *
 * This is the core editor UI extracted from `CardFieldsPopoverContent` so it
 * can be rendered in both the standalone modal (CardFieldsPopover) and the
 * "Card Layout" tab of the pipeline editor slideout (StagesSlideout).
 *
 * It renders:
 *  - A DnD-sortable field list with add/remove/reorder
 *  - A live card preview showing fake data
 *  - An "Add field" button
 *  - A screen-reader live region for announcements
 *
 * It does NOT render any wrapper (Dialog, SlideoutMenu) or footer buttons.
 * The parent is responsible for providing save/cancel/reset actions.
 */

import { closestCenter, DndContext } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { Plus } from '@untitledui/icons';
import { useMemo } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { useMultiValueSensors } from '@/lib/crm/utils/multiValueDnd';

import type { CardFieldsDraftResult } from '../hooks/useCardFieldsDraft';
import type { FieldPickerOption } from '../utils/cardConfigOptions';
import { CardFieldRow } from './CardFieldRow';
import { CardPreview } from './CardPreview';

// ── Module-level constants ───────────────────────────────────────────

const BODY_CLASS = 'overflow-y-auto px-4 py-3 space-y-2';

const EMPTY_STATE_CLASS =
  'py-6 text-sm text-[var(--color-text-tertiary)] text-center';

// ── Props ────────────────────────────────────────────────────────────

export interface CardFieldsEditorBodyProps {
  /** Draft state from useCardFieldsDraft */
  draftState: CardFieldsDraftResult;
  /** Available property field options */
  properties: FieldPickerOption[];
  /** Available association field options */
  associations: FieldPickerOption[];
  /** Singular label for the entity type (e.g. "Deal", "Note"). */
  entityLabel?: string;
  /**
   * Extra options for configured fields that are NOT picker-eligible
   * (e.g. markdown properties). These are merged into the preview's
   * options map so configured slots render properly, but they do NOT
   * appear in the "Add field" picker dropdown.
   */
  supplementalOptions?: FieldPickerOption[];
}

/** Props for the card layout tab in pipeline slideouts. Extends editor body props with save/reset callbacks. */
export interface CardLayoutTabProps extends CardFieldsEditorBodyProps {
  onSaveCardConfig: () => void;
  onResetCardConfig: () => void;
}

// ── Component ────────────────────────────────────────────────────────

export function CardFieldsEditorBody({
  draftState,
  properties,
  associations,
  entityLabel,
  supplementalOptions,
}: CardFieldsEditorBodyProps) {
  const {
    draft,
    rowIds,
    announcement,
    addButtonRef,
    handleAddRow,
    handleRemoveRow,
    handleDragEnd,
    handleSetSlotField,
    handleAddSlot,
    handleRemoveSlot,
    handleAddFallback,
    handleSetFallbackField,
    handleRemoveFallback,
    handleToggleSlotLabel,
  } = draftState;

  const sensors = useMultiValueSensors();

  // Build a lookup map for the preview: `${type}:${name}` → FieldPickerOption
  // Includes both picker-eligible fields and supplemental options for
  // configured-but-ineligible fields (e.g. markdown properties like ahoy_content).
  const optionsByKey = useMemo(() => {
    const map = new Map<string, FieldPickerOption>();
    for (const opt of properties) {
      map.set(`${opt.type}:${opt.name}`, opt);
    }
    for (const opt of associations) {
      map.set(`${opt.type}:${opt.name}`, opt);
    }
    if (supplementalOptions) {
      for (const opt of supplementalOptions) {
        if (!map.has(`${opt.type}:${opt.name}`)) {
          map.set(`${opt.type}:${opt.name}`, opt);
        }
      }
    }
    return map;
  }, [properties, associations, supplementalOptions]);

  return (
    <div className='flex flex-col gap-6'>
      {/* Card preview */}
      <div className='flex flex-col items-center px-4'>
        <span className='text-caption text-[var(--color-text-tertiary)] mb-2'>
          Preview
        </span>
        <CardPreview
          config={draft}
          options={optionsByKey}
          entityLabel={entityLabel}
        />
      </div>

      {/* Field configuration */}
      <div className='border-t border-[var(--color-border-secondary)] pt-4'>
        <p className='px-4 pb-3 text-sm text-[var(--color-text-tertiary)]'>
          Each row shows up to two fields side by side on the card. Add a
          fallback to display an alternative when the primary field is empty.
          Drag rows to reorder.
        </p>

        {/* Screen-reader live region */}
        <div aria-live='polite' className='sr-only'>
          {announcement}
        </div>

        {/* Field list */}
        <div className={BODY_CLASS}>
          {draft.rows.length === 0 ? (
            <p className={EMPTY_STATE_CLASS}>
              No fields shown. Cards display just the title and footer. Add a
              field below.
            </p>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={rowIds}
                strategy={verticalListSortingStrategy}
              >
                {draft.rows.map((row, i) => (
                  <CardFieldRow
                    key={rowIds[i]}
                    id={rowIds[i]}
                    rowIndex={i}
                    row={row}
                    properties={properties}
                    associations={associations}
                    onSetSlotField={handleSetSlotField}
                    onAddSlot={handleAddSlot}
                    onRemoveRow={handleRemoveRow}
                    onRemoveSlot={handleRemoveSlot}
                    onAddFallback={handleAddFallback}
                    onSetFallbackField={handleSetFallbackField}
                    onRemoveFallback={handleRemoveFallback}
                    onToggleSlotLabel={handleToggleSlotLabel}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </div>

        {/* Add field */}
        <div className='px-4 pt-3 pb-1'>
          <Button
            ref={addButtonRef}
            color='link-gray'
            size='sm'
            iconLeading={Plus}
            onClick={handleAddRow}
            aria-label='Add field'
          >
            Add field
          </Button>
        </div>
      </div>
    </div>
  );
}
